"use client";

import { useEffect } from "react";
import { trackViewItem, whenGtagReady } from "@/lib/analytics";

/**
 * Fires the GA4 `view_item` event for a product page/overlay, then renders
 * nothing.
 *
 * `trackViewItem` had ZERO callers before this component existed, so GA4
 * received no product-view data at all. Drop this into a product detail view (a
 * server component can render it — it's a client component itself) and
 * product-view analytics start working.
 *
 * The events are consumed in GA4's own UI (Reports → Monetisation → item
 * views). This repo does NOT render a views-ranked product card: the admin
 * top-products card ranks by revenue, and the GA4 `itemsViewed` report that
 * once backed a dashboard card was removed along with that card.
 *
 * PRICE UNIT: `pricePence` is integer PENCE, matching every helper in
 * `lib/analytics.ts` and the shape the DB/cart already carry. One convention
 * across the module, on purpose — see the PRICE UNITS block there.
 *
 * The send goes through `whenGtagReady`: gtag loads `lazyOnload`, so a bare
 * `trackViewItem` on mount is a coin-flip against the script arriving first
 * (that's the same race that was losing `purchase` events).
 */
export function ViewItemBeacon({
  id,
  name,
  pricePence,
  category,
  variant,
}: {
  id: string;
  name: string;
  /** Price in integer PENCE (e.g. 4250) — NOT pounds. */
  pricePence?: number | null;
  category: string;
  variant?: string;
}) {
  useEffect(() => {
    return whenGtagReady(() => {
      trackViewItem({
        productId: id,
        title: name,
        category,
        variant,
        pricePence: pricePence ?? null,
      });
    });
  }, [id, name, pricePence, category, variant]);

  return null;
}
